import { Journey } from '@audibene/ta.utils-opportunity'
import { AcfuProgressiveDisclosureParameters, AppointmentType, Country, EventType } from '../../../../constants'
import { EventTrackingFunction } from '../../../../hooks'
import { AcfuActingUserRole, AppointmentFormData, EventData } from '../../../../types'
import { isAcfuProgressiveDisclosureEligible } from '../../../../util'

interface Props {
  actingUserId?: string
  actingUserRole: AcfuActingUserRole
  data: AppointmentFormData
  eventData: EventData
  isAcfuProgressiveDisclosureEnabled?: boolean
  // Required (not optional) so a caller can't silently forget it — this is exactly the class of
  // bug a reviewer caught: schedule/New/index.tsx's submit handler serves both create and
  // reschedule, and this function must never fire ACFU analytics for a reschedule.
  isRescheduling: boolean
  trackEvent: EventTrackingFunction
}

// PX-3649: fires the two ACFU-only analytics events (booked + extended-window-reason) on a
// successful appointment submission — shared by both booking flows (/consultant and /schedule),
// which otherwise carried an identical inlined block each.
const trackAcfuBookingOutcome = ({
  actingUserId,
  actingUserRole,
  data,
  eventData,
  isAcfuProgressiveDisclosureEnabled,
  isRescheduling,
  trackEvent,
}: Props) => {
  const isEligible = isAcfuProgressiveDisclosureEligible({
    appointmentType: data.type as AppointmentType,
    country: data.country as Country,
    isFlagEnabled: isAcfuProgressiveDisclosureEnabled,
    isRescheduling,
    journey: data.journey as Journey,
  })

  if (!isEligible) return

  // Derived from the real booked date (eventData.daysInTheFuture, already computed by every
  // caller for the base APPOINTMENT_FORM_SUBMISSION_SUCCEEDED event) rather than from
  // acfuVisibleWindowDays — the window-expansion state doesn't tell you where the booked date
  // actually landed (expand, reconsider, book tomorrow anyway is a true within-window booking).
  const daysInTheFuture = eventData.daysInTheFuture
  const isWithinDefaultWindow =
    typeof daysInTheFuture === 'number' ? daysInTheFuture <= AcfuProgressiveDisclosureParameters.DEFAULT_WINDOW_IN_DAYS : undefined

  trackEvent(EventType.ACFU_APPOINTMENT_BOOKED, {
    data: {
      ...eventData,
      actingUserRole,
      isWithinDefaultWindow,
      showMoreClicksToReach: (data.acfuShowMoreClickCount as number) ?? 0,
      userId: actingUserId,
    },
  })

  if (data.acfuExtendedWindowReason) {
    trackEvent(EventType.ACFU_EXTENDED_WINDOW_REASON_PROVIDED, {
      data: {
        ...eventData,
        actingUserRole,
        reason: data.acfuExtendedWindowReason as string,
        userId: actingUserId,
      },
    })
  }
}

export { trackAcfuBookingOutcome }
